/**
 * Custom NumberStatsComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { map, of } from 'rxjs';
import { NumberStatsComponent as ParentNumberStatsComponent } from '@shared-lib/global-components/number-stats/number-stats.component';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { environment } from '../../../projects/my-app/src/environments/environment';

interface SummaryStatSection {
  label: string;
  value: string;
  color?: string;
  fontSize?: string;
  labelFontSize?: string;
}

@Component({
  selector: 'app-number-stats',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './number-stats.component.html',
  styleUrls: ['./number-stats.component.scss']
})
export class NumberStatsComponent extends ParentNumberStatsComponent {
  private readonly customGlobalFilterService: GlobalFilterService;
  private readonly customChartFilterHandler: ChartFilterHandlerService;
  summarySections: SummaryStatSection[] = [];

  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    chartFilterHandler: ChartFilterHandlerService,
  ) {
    super(configService, globalFilterService, chartFilterHandler);
    this.customGlobalFilterService = globalFilterService;
    this.customChartFilterHandler = chartFilterHandler;
  }

  override ngAfterViewInit(): void {
    this.applyInputDefault();
    super.ngAfterViewInit();
  }

  get resolvedFontSize(): string {
    return String(this.data?.fontSize ?? this.data?.fontsize ?? '').trim();
  }

  get resolvedValueFontSize(): string {
    return String(this.data?.valueFontSize ?? this.data?.statFontSize ?? '').trim();
  }

  get resolvedValueFontScale(): string {
    const scale = Number(this.data?.valueFontScale ?? this.data?.statFontScale);
    return Number.isFinite(scale) && scale > 0 ? String(scale) : '';
  }

  get resolvedCardHeight(): string {
    return String(this.data?.cardHeight ?? this.data?.statCardHeight ?? this.data?.height ?? '').trim();
  }

  get isEditableNumberStat(): boolean {
    return this.userInputEnabled && this.isInputNumberStat && this.data?.editable !== false;
  }

  get shouldShowErrorIcon(): boolean {
    return this.hasError && this.data?.hideErrorIcon !== true;
  }

  get isSummaryStatsLayout(): boolean {
    const explicitSummaryFlag = this.data?.useSummaryStats ?? this.data?.summaryStats ?? this.data?.useSplitStats;
    const normalizedFlag = typeof explicitSummaryFlag === 'string'
      ? explicitSummaryFlag.trim().toLowerCase()
      : explicitSummaryFlag;

    if (normalizedFlag === false || normalizedFlag === 'false') {
      return false;
    }

    return normalizedFlag === true
      || normalizedFlag === 'true'
      || this.data?.numberStatsVariant === 'summary'
      || this.data?.displayMode === 'summary-stats';
  }

  get summaryTitleFontSize(): string {
    return String(
      this.data?.summaryTitleFontSize ??
      this.data?.titleFontSize ??
      '',
    ).trim();
  }

  get summaryLabelFontSize(): string {
    return String(
      this.data?.summaryLabelFontSize ??
      this.data?.sectionLabelFontSize ??
      '',
    ).trim();
  }

  get summaryValueFontSize(): string {
    return String(
      this.data?.summaryValueFontSize ??
      this.data?.sectionValueFontSize ??
      this.data?.valueFontSize ??
      '',
    ).trim();
  }

  get summaryValueColor(): string {
    return String(this.data?.summaryValueColor ?? this.data?.valueColor ?? '').trim();
  }

  override onInputStatEnter(): any {
    if (!this.isEditableNumberStat) {
      return;
    }

    const key = String(this.data?.key || '').trim();
    if (!key) {
      return;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters();
    const customerName = currentFilters?.requestParams?.customer_name;
    if (customerName !== undefined && !Array.isArray(customerName)) {
      this.customGlobalFilterService.updateFilterSelection(
        'customer_name',
        [customerName],
        false,
        undefined,
        false,
      );
    }

    this.customChartFilterHandler.updateFilterSelection(
      { ...this.data, filterKey: key },
      { [key]: this.statInputValue },
      { [key]: this.statInputValue },
    );
  }

  private applyInputDefault(): void {
    if (!this.data?.inputNumberStat) {
      return;
    }

    const filterKey = this.data?.filterKey || this.data?.key;
    const defaultValue = this.data?.inputDefaultValue;

    if (!filterKey || defaultValue === undefined || defaultValue === null) {
      return;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters();
    const existingValue =
      currentFilters?.requestParams?.[filterKey] ?? currentFilters?.queryParams?.[filterKey];

    if (existingValue !== undefined) {
      return;
    }

    const isQueryParam = Boolean(this.data?.useQueryParams);
    this.customGlobalFilterService.updateFilterSelection(
      filterKey,
      [defaultValue],
      isQueryParam,
      this.data?.widgetId,
      false,
      this.data?.filterListener,
    );
  }

  override onStatInputChange(value: any): any {
    // TODO: Add your custom logic here
    return super.onStatInputChange(value);
  }

  override loadStat(filters?: any): any {
    if (this.isReadOnlyInputNumberStat) {
      return of({ [this.data?.key]: this.readOnlyInputValue });
    }

    return super.loadStat(filters).pipe(
      map((response: any) => {
        response = this.normalizeStatResponseForKey(response);
        response = this.applyAxisDriverStatusStat(response);
        this.syncResponseValueToFilter(response);
        this.captureSummarySections(response);
        return this.applyIncludeMFormatting(response);
      }),
    );
  }

  protected override formatValue(value: any): string {
    if (this.data?.formatMode === 'currency-thousands') {
      return this.formatCurrencyThousands(
        value,
        this.data?.prefix || '$',
        this.data?.suffix || 'K',
        this.data?.maximumDigits ?? this.data?.maxDigits ?? this.data?.valueDigits,
        this.isAutoCurrencyScale(this.data),
      );
    }

    if (this.data?.useFormatStat) {
      return this.formatStatWithDecimals(value, this.data?.key || '', this.data?.prefix || '');
    }

    return super.formatValue(value);
  }

  private formatCurrencyThousands(
    value: any,
    prefix: string = '$',
    suffix: string = 'K',
    maximumDigits?: any,
    autoScale: boolean = false,
  ): string {
    if (value === null || value === undefined || value === '') return '-';

    const numericValue = Number(value);
    if (isNaN(numericValue)) return '-';

    if (autoScale && Math.abs(numericValue) >= 1e6) {
      return `${prefix}${this.formatMillionsValue(numericValue)}M`;
    }

    const thousandsValue = numericValue / 1e3;
    const roundedValue = Math.round(thousandsValue);
    const normalizedMaximumDigits = Number(maximumDigits);
    const formattedValue = Number.isInteger(normalizedMaximumDigits) && normalizedMaximumDigits > 0
      ? this.limitNumericDigits(roundedValue, normalizedMaximumDigits)
      : String(roundedValue);

    return `${prefix}${formattedValue}${suffix}`;
  }

  private limitNumericDigits(value: number, maximumDigits: number): string {
    const sign = value < 0 ? '-' : '';
    const digits = String(Math.abs(value));
    return `${sign}${digits.slice(0, maximumDigits)}`;
  }

  private formatMillionsValue(value: number): string {
    const millionValue = value / 1e6;
    const absMillionValue = Math.abs(millionValue);
    const decimalPlaces = absMillionValue < 10 ? 2 : 1;
    return millionValue.toFixed(decimalPlaces);
  }

  private isAutoCurrencyScale(config: any): boolean {
    const scaleMode = String(config?.scaleMode ?? config?.unitMode ?? config?.currencyScale ?? '').trim().toLowerCase();
    return config?.autoScale === true || scaleMode === 'auto';
  }

  private formatStatWithDecimals(value: any, key: string = '', prefix: string = ''): string {
    if (value === null || value === undefined || value === '') return '-';

    const numericValue = Number(value);
    if (isNaN(numericValue)) return '-';

    const normalizedKey = key.toLowerCase();
    const isPercentage = key.includes('%') || normalizedKey.includes('percent') || normalizedKey.includes('percentage');
    if (isPercentage) {
      const decimals = this.resolveDecimalPlaces(this.data?.decimals ?? this.data?.decimalPlaces, 1);
      return `${numericValue.toFixed(decimals)}${this.data?.suffix || '%'}`;
    }

    const absValue = Math.abs(numericValue);
    let formattedValue = '';
    let suffix = '';

    if (absValue >= 1e9) {
      formattedValue = (numericValue / 1e9).toFixed(2);
      suffix = 'B';
    } else if (absValue >= 1e6) {
      formattedValue = (numericValue / 1e6).toFixed(2);
      suffix = 'M';
    } else if (absValue >= 1e3) {
      formattedValue = (numericValue / 1e3).toFixed(2);
      suffix = 'K';
    } else {
      formattedValue = numericValue.toFixed(2);
    }

    // Remove .00 if present
    if (formattedValue.endsWith('.00')) {
      formattedValue = formattedValue.slice(0, -3);
    }

    return `${prefix}${formattedValue}${suffix}`;
  }

  private resolveDecimalPlaces(value: any, fallback: number): number {
    const decimals = Number(value);
    if (!Number.isInteger(decimals) || decimals < 0) {
      return fallback;
    }

    return Math.min(decimals, 10);
  }

  private applyIncludeMFormatting(response: any): any {
    if (!this.data?.includem || !this.data?.key || this.data?.useFormatStat || this.isInputNumberStat) {
      return response;
    }

    const key = this.data.key;
    const directValue = response?.[key];
    if (directValue !== undefined) {
      return {
        ...response,
        [key]: this.formatIncludeMValue(directValue),
      };
    }

    const nestedValue = response?.responseList?.[0]?.data?.[0]?.[key];
    if (nestedValue === undefined) {
      return response;
    }

    const responseList = [...(response.responseList || [])];
    const firstResponse = { ...(responseList[0] || {}) };
    const data = [...(firstResponse.data || [])];
    data[0] = {
      ...(data[0] || {}),
      [key]: this.formatIncludeMValue(nestedValue),
    };
    firstResponse.data = data;
    responseList[0] = firstResponse;

    return {
      ...response,
      responseList,
    };
  }

  private normalizeStatResponseForKey(response: any): any {
    const key = String(this.data?.key || '').trim();
    if (!key || this.hasValueAtKey(response, key)) {
      return response;
    }

    const configuredPath = String(this.data?.dataPath || this.data?.responsePath || '').trim();
    const configuredSource = configuredPath ? this.getValueByPath(response, configuredPath) : undefined;
    const valueFromConfiguredPath = this.findValueForKey(configuredSource, key);
    if (valueFromConfiguredPath !== undefined) {
      return {
        ...(configuredSource && typeof configuredSource === 'object' && !Array.isArray(configuredSource)
          ? configuredSource
          : {}),
        [key]: valueFromConfiguredPath,
      };
    }

    const valueFromResponse = this.findValueForKey(response, key);
    return valueFromResponse !== undefined ? { [key]: valueFromResponse } : response;
  }

  private hasValueAtKey(source: any, key: string): boolean {
    return source
      && typeof source === 'object'
      && !Array.isArray(source)
      && Object.prototype.hasOwnProperty.call(source, key)
      && source[key] !== undefined
      && source[key] !== null
      && source[key] !== '';
  }

  private findValueForKey(source: any, key: string): any {
    if (source === null || source === undefined) {
      return undefined;
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        const value = this.findValueForKey(item, key);
        if (value !== undefined) {
          return value;
        }
      }
      return undefined;
    }

    if (typeof source !== 'object') {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined && source[key] !== null && source[key] !== '') {
      return source[key];
    }

    for (const nestedValue of Object.values(source)) {
      const value = this.findValueForKey(nestedValue, key);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  private applyAxisDriverStatusStat(response: any): any {
    const driverLabel = String(
      this.data?.axisDriverStatusLabel ??
      this.data?.statusFromAxisDriverLabel ??
      '',
    ).trim();

    if (!driverLabel || !this.data?.key) {
      return response;
    }

    const rows = this.getAxisDriverRows(response);
    const labelKey = this.data?.labelKey || 'label';
    const statusKey = this.data?.statusKey || 'status';
    const normalizedLabel = this.normalizeLookup(driverLabel);
    const matchedRow = rows.find((row: any) => {
      const rowLabel = row?.[labelKey] ?? row?.name ?? row?.factor;
      return this.normalizeLookup(rowLabel) === normalizedLabel;
    });
    const status = matchedRow?.[statusKey] ?? matchedRow?.value ?? matchedRow?.rating;

    if (status === undefined || status === null || status === '') {
      return response;
    }

    return {
      ...(response && typeof response === 'object' && !Array.isArray(response) ? response : {}),
      [this.data.key]: status,
    };
  }

  private getAxisDriverRows(response: any): any[] {
    const dataPath = String(
      this.data?.axisDriverDataPath ??
      this.data?.dataPath ??
      this.data?.responsePath ??
      '',
    ).trim();

    const candidates = [
      dataPath ? this.getValueByPath(response, dataPath) : undefined,
      dataPath ? this.getValueByPath(response, `responseList.0.data.0.${dataPath}`) : undefined,
      dataPath ? this.getValueByPath(response, `data.0.${dataPath}`) : undefined,
      response?.rows,
      response?.items,
      response?.data,
      this.getValueByPath(response, 'responseList.0.data.0.rows'),
      this.getValueByPath(response, 'responseList.0.data.0.items'),
      this.getValueByPath(response, 'responseList.0.data.0'),
      this.getValueByPath(response, 'responseList.0.data'),
      response,
    ];

    for (const candidate of candidates) {
      const rows = this.toAxisDriverRows(candidate);
      if (rows.length) {
        return rows;
      }
    }

    return [];
  }

  private toAxisDriverRows(candidate: any): any[] {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      for (const key of ['rows', 'items', 'data']) {
        const rows = this.toAxisDriverRows(candidate[key]);
        if (rows.length) {
          return rows;
        }
      }
    }

    return Array.isArray(candidate)
      ? candidate.filter((item) => item && typeof item === 'object' && !Array.isArray(item))
      : [];
  }

  private normalizeLookup(value: any): string {
    return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private get isReadOnlyInputNumberStat(): boolean {
    return this.isInputNumberStat && this.data?.editable === false;
  }

  private get readOnlyInputValue(): any {
    return this.data?.inputDefaultValue ?? this.data?.defaultValue ?? this.data?.value ?? '';
  }

  private captureSummarySections(response: any): void {
    if (!this.isSummaryStatsLayout || !Array.isArray(this.data?.statSections)) {
      this.summarySections = [];
      return;
    }

    const source = this.resolveResponseSource(response);
    this.summarySections = this.data.statSections.map((section: any) => ({
      label: String(section?.label ?? ''),
      value: this.formatSectionValue(source?.[section?.key], section),
      color: String(section?.color ?? section?.valueColor ?? '').trim(),
      fontSize: String(section?.fontSize ?? section?.valueFontSize ?? this.summaryValueFontSize ?? '').trim(),
      labelFontSize: String(section?.labelFontSize ?? this.summaryLabelFontSize ?? '').trim(),
    }));
  }

  private resolveResponseSource(response: any): any {
    const configuredPath = String(this.data?.dataPath || this.data?.responsePath || '').trim();
    const hasDirectStatKey = !!this.data?.key
      && response
      && typeof response === 'object'
      && !Array.isArray(response)
      && Object.prototype.hasOwnProperty.call(response, this.data.key);

    let normalized = hasDirectStatKey
      ? response
      : configuredPath
        ? this.getValueByPath(response, configuredPath)
        : response?.responseList?.[0]?.data?.[0] ?? response;

    if (Array.isArray(normalized)) {
      normalized = normalized[0] ?? {};
    }

    return this.data?.parentKey
      ? normalized?.[this.data.parentKey] ?? response?.[this.data.parentKey] ?? normalized
      : normalized;
  }

  private getValueByPath(source: any, path: string): any {
    return path.split('.').reduce((current: any, segment: string) => current?.[segment], source);
  }

  private syncResponseValueToFilter(response: any): void {
    if (this.data?.syncResponseValueToFilter !== true) {
      return;
    }

    const filterKey = String(this.data?.syncResponseFilterKey || this.data?.filterKey || this.data?.key || '').trim();
    if (!filterKey) {
      return;
    }

    const source = this.resolveResponseSource(response);
    const responseKey = String(this.data?.syncResponseValueKey || this.data?.key || '').trim();
    const value = responseKey ? this.findValueForKey(source, responseKey) : undefined;
    if (value === undefined || value === null || value === '') {
      return;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters();
    const currentValue =
      currentFilters?.requestParams?.[filterKey] ??
      currentFilters?.queryParams?.[filterKey];

    if (String(currentValue ?? '').trim() === String(value).trim()) {
      return;
    }

    this.customGlobalFilterService.updateFilterSelection(
      filterKey,
      [value],
      Boolean(this.data?.useQueryParams),
      this.data?.widgetId,
      true,
      this.data?.filterListener,
    );
  }

  private formatSectionValue(value: any, section: any): string {
    if (section?.formatMode === 'currency-thousands') {
      return this.formatCurrencyThousands(
        value,
        section?.prefix || '$',
        section?.suffix || 'K',
        section?.maximumDigits ?? section?.maxDigits ?? section?.valueDigits,
        this.isAutoCurrencyScale(section),
      );
    }

    if (section?.useFormatStat) {
      return this.formatStatWithDecimals(value, section?.key || '', section?.prefix || '');
    }

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numericValue = Number(value);
    const displayValue = Number.isFinite(numericValue) && !Number.isInteger(numericValue)
      ? numericValue.toFixed(2)
      : value;

    return `${section?.prefix || ''}${displayValue}${section?.suffix || ''}`;
  }

  private formatIncludeMValue(value: any): string {
    const stringValue = String(value ?? '').trim();
    if (!stringValue) {
      return stringValue;
    }

    const withoutPrefix = stringValue.startsWith('$') ? stringValue.slice(1) : stringValue;
    const withoutSuffix = /m$/i.test(withoutPrefix) ? withoutPrefix.slice(0, -1) : withoutPrefix;
    return `$${withoutSuffix}M`;
  }

  private get userInputEnabled(): boolean {
    return this.resolveBooleanFlag((environment as any).USER_INPUT_ENABLED) ?? true;
  }

  private resolveBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }
      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }
}
